import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { refMarche as api } from '../services/api';
import './Objectifs.css';
import './Stock.css';
import './ReferentielMarche.css';

// ─── Référentiel Marché — écran d'administration ────────────────────────────
// Remplace le ⚙️ Référentiel du rapport Marché du CRM. Deux onglets :
//   · Marques : constructeur et origine de chaque marque immatriculée ;
//   · Affectations : par département, le groupe distributeur de chaque marque
//     par PÉRIODES DATÉES, et la concession.
// Écriture réservée aux administrateurs ; tout le monde peut consulter.
//
// Le groupe ne se déduit plus des données (le Google Sheet ne le porte plus
// depuis 2026) : la couverture affichée dit quelle part du marché reste « Non
// rattachée » dans le rapport, et le tri par volume met en tête les marques qui
// pèsent.

const nf = new Intl.NumberFormat('fr-FR');
const nb = (v) => nf.format(Math.round(Number(v) || 0));
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
const premierDuMois = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const veille = (iso) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); };

export default function ReferentielMarche() {
  const [onglet, setOnglet] = useState('marques');
  return (
    <main className="ob-page rm-page">
      <div className="ob-head">
        <h1>Référentiel Marché</h1>
        <p className="ob-sub">Rattachements utilisés par le rapport Marché — Immatriculations : constructeur et origine de chaque marque, groupe distributeur et concession par département.</p>
      </div>
      <div className="rm-onglets">
        <span className="st-seg">
          <button type="button" className={onglet === 'marques' ? 'on' : ''} onClick={() => setOnglet('marques')}>Marques</button>
          <button type="button" className={onglet === 'affectations' ? 'on' : ''} onClick={() => setOnglet('affectations')}>Affectations</button>
        </span>
      </div>
      {onglet === 'marques' ? <Marques /> : <Affectations />}
    </main>
  );
}

// ── Onglet Marques ──────────────────────────────────────────────────────────
function Marques() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [modifs, setModifs] = useState({});      // marque → { constructeur, origine }
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    api.marques().then((r) => { setData(r.data); setModifs({}); }).catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement'));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const liste = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (data?.marques || []).filter((m) => !q || m.marque.toLowerCase().includes(q)
      || (m.constructeur || '').toLowerCase().includes(q));
  }, [data, recherche]);

  if (erreur) return <div className="ob-erreur">{erreur}</div>;
  if (!data) return <div className="ob-vide">Chargement…</div>;
  const editable = data.editable;
  const val = (m, c) => (modifs[m.marque] && c in modifs[m.marque] ? modifs[m.marque][c] : (m[c] || ''));
  const change = (m, c, v) => setModifs((x) => ({ ...x, [m.marque]: { ...(x[m.marque] || {}), [c]: v } }));
  const modifiee = (m) => modifs[m.marque] && ['constructeur', 'origine'].some((c) => c in modifs[m.marque] && (modifs[m.marque][c] || '') !== (m[c] || ''));
  const nbModif = (data.marques || []).filter(modifiee).length;
  const sansConstructeur = data.marques.filter((m) => m.v12 > 0 && !m.constructeur);
  const sansOrigine = data.marques.filter((m) => m.v12 > 0 && !m.origine);

  const enregistrer = async () => {
    setEnvoi(true); setEtat('');
    try {
      const items = data.marques.filter(modifiee).map((m) => ({ marque: m.marque, constructeur: val(m, 'constructeur'), origine: val(m, 'origine') }));
      const r = await api.enregistrerMarques(items);
      setEtat(`${r.data.modifiees} marque${r.data.modifiees > 1 ? 's' : ''} enregistrée${r.data.modifiees > 1 ? 's' : ''}.`);
      charger();
    } catch (e) { setEtat(e.response?.data?.error || 'Erreur lors de l\'enregistrement'); } finally { setEnvoi(false); }
  };

  return (
    <div className="ob-card">
      <div className="ob-card-h">
        <h2>Marques <span className="rm-compte">{data.marques.length}</span></h2>
        <div className="ob-tools">
          <span className="rm-hint">sur les 12 derniers mois : {sansConstructeur.length} marque{sansConstructeur.length > 1 ? 's' : ''} immatriculée{sansConstructeur.length > 1 ? 's' : ''} sans constructeur, {sansOrigine.length} sans origine</span>
          <input className="rm-recherche" placeholder="Rechercher une marque ou un constructeur…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          {editable && <button type="button" className="ob-btn ob-btn-p" disabled={!nbModif || envoi} onClick={enregistrer}>{envoi ? 'Enregistrement…' : `Enregistrer${nbModif ? ` (${nbModif})` : ''}`}</button>}
        </div>
      </div>
      <datalist id="rm-constructeurs">{data.constructeurs.map((c) => <option key={c} value={c} />)}</datalist>
      <div className="ob-scroll rm-scroll">
        <table className="ob-tbl rm-tbl">
          <thead><tr>
            <th className="l">Marque</th><th>Immat. 12 mois</th><th>Depuis 2019</th>
            <th className="l">Constructeur</th><th className="l">Origine</th><th className="l">Dernière modification</th>
          </tr></thead>
          <tbody>
            {liste.map((m) => (
              <tr key={m.marque} className={modifiee(m) ? 'rm-modifiee' : ''}>
                <td className="l"><b>{m.marque}</b></td>
                <td>{m.v12 ? nb(m.v12) : <span className="ob-faint">—</span>}</td>
                <td className="ob-faint">{nb(m.vtot)}</td>
                <td className="l">
                  <input list="rm-constructeurs" value={val(m, 'constructeur')} disabled={!editable}
                    className={!val(m, 'constructeur') && m.v12 ? 'rm-manquant' : ''}
                    placeholder="—" onChange={(e) => change(m, 'constructeur', e.target.value)} />
                </td>
                <td className="l">
                  <select value={val(m, 'origine')} disabled={!editable}
                    className={!val(m, 'origine') && m.v12 ? 'rm-manquant' : ''}
                    onChange={(e) => change(m, 'origine', e.target.value)}>
                    <option value="">—</option>
                    {data.origines.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td className="l ob-rev">{m.majPar ? <>{dateFr(m.majLe)} · {m.majPar}</> : <span className="ob-faint">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ob-note">{!editable ? 'Lecture seule : la modification du référentiel est réservée aux administrateurs.' : etat || (nbModif ? <b className="ob-accent">{nbModif} marque{nbModif > 1 ? 's' : ''} modifiée{nbModif > 1 ? 's' : ''} — non enregistrée{nbModif > 1 ? 's' : ''}.</b> : 'Une marque sans constructeur ou sans origine apparaît « Non classé » dans le rapport.')}</p>
    </div>
  );
}

// ── Onglet Affectations ─────────────────────────────────────────────────────
const DEPTS = ['MARTINIQUE', 'GUADELOUPE', 'GUYANE', 'REUNION', 'MAYOTTE', 'ST PIERRE ET MI.'];

function Affectations() {
  const [dept, setDept] = useState('MARTINIQUE');
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouverte, setOuverte] = useState(null);   // marque dépliée
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(() => {
    setErreur('');
    api.affectations(dept).then((r) => setData(r.data)).catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement'));
  }, [dept]);
  useEffect(() => { setData(null); setOuverte(null); charger(); }, [charger]);

  const liste = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (data?.lignes || []).filter((l) => !q || l.marque.toLowerCase().includes(q) || (l.groupe || '').toLowerCase().includes(q));
  }, [data, recherche]);

  return (
    <div className="ob-card">
      <div className="ob-card-h">
        <h2>Affectations par département</h2>
        <div className="ob-tools">
          <span className="st-seg">{DEPTS.map((d) => (
            <button key={d} type="button" className={dept === d ? 'on' : ''} onClick={() => setDept(d)}>{d === 'ST PIERRE ET MI.' ? 'St-Pierre' : d.charAt(0) + d.slice(1).toLowerCase()}</button>
          ))}</span>
          <input className="rm-recherche" placeholder="Rechercher une marque ou un groupe…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
        </div>
      </div>
      {erreur ? <div className="ob-erreur">{erreur}</div> : !data ? <div className="ob-vide">Chargement…</div> : (
        <>
          <div className="rm-couv">
            {data.couverture.pct == null ? 'Aucune immatriculation sur les 12 derniers mois.' : (
              <><b className={data.couverture.pct < 90 ? 'rm-alerte' : ''}>{data.couverture.pct.toLocaleString('fr-FR')} %</b> du marché des 12 derniers mois
                a un groupe dans ce département ({nb(data.couverture.couvert)} immatriculations sur {nb(data.couverture.total)}).
                Le reste apparaît « Non rattaché » dans le rapport.</>
            )}
          </div>
          <datalist id="rm-groupes">{data.groupes.map((g) => <option key={g} value={g} />)}</datalist>
          <div className="ob-scroll rm-scroll">
            <table className="ob-tbl rm-tbl">
              <thead><tr>
                <th className="l">Marque</th><th>Immat. 12 mois</th><th className="l">Groupe actuel</th><th className="l">Depuis</th>
                <th className="l">Concession</th><th className="l">Historique</th>
              </tr></thead>
              <tbody>
                {liste.map((l) => (
                  <Ligne key={l.marque} l={l} dept={dept} editable={data.editable}
                    ouverte={ouverte === l.marque} basculer={() => setOuverte((o) => (o === l.marque ? null : l.marque))}
                    recharger={charger} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="ob-note">{!data.editable
            ? 'Lecture seule : la modification du référentiel est réservée aux administrateurs.'
            : 'Changer le groupe actuel corrige la période en cours. Pour un changement de distributeur, dépliez la marque et ajoutez une nouvelle période : la précédente est fermée la veille.'}</p>
        </>
      )}
    </div>
  );
}

// Une marque × département : groupe actuel, concession, périodes dépliables.
function Ligne({ l, dept, editable, ouverte, basculer, recharger }) {
  const init = useMemo(() => ({
    periodes: l.periodes.map((p) => ({ groupe: p.groupe, debut: p.debut, fin: p.fin || '' })),
    concession: l.concession || '',
  }), [l]);
  const [f, setF] = useState(init);
  const [msg, setMsg] = useState('');
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { setF(init); setMsg(''); }, [init]);

  const modifie = JSON.stringify(f) !== JSON.stringify(init);
  const idxActuel = (() => { const i = f.periodes.findIndex((p) => !p.fin); return i >= 0 ? i : f.periodes.length - 1; })();
  const actuel = f.periodes[idxActuel];
  const setPeriode = (i, c, v) => setF((x) => ({ ...x, periodes: x.periodes.map((p, j) => (j === i ? { ...p, [c]: v } : p)) }));
  // Groupe actuel : sans période, on en crée une ouverte depuis 2019 (début des données).
  const setGroupeActuel = (v) => {
    if (!f.periodes.length) setF((x) => ({ ...x, periodes: [{ groupe: v, debut: '2019-01-01', fin: '' }] }));
    else setPeriode(idxActuel, 'groupe', v);
  };
  // Nouvelle période : commence le 1er du mois courant, la période ouverte est fermée la veille.
  const ajouter = () => setF((x) => {
    const debut = premierDuMois();
    const ps = x.periodes.map((p) => (!p.fin && p.debut < debut ? { ...p, fin: veille(debut) } : p));
    return { ...x, periodes: [...ps, { groupe: '', debut, fin: '' }] };
  });
  const supprimer = (i) => setF((x) => ({ ...x, periodes: x.periodes.filter((_, j) => j !== i) }));

  const enregistrer = async () => {
    setEnvoi(true); setMsg('');
    try {
      await api.enregistrerAffectation({ marque: l.marque, departement: dept, concession: f.concession,
        periodes: f.periodes.map((p) => ({ groupe: p.groupe, debut: p.debut, fin: p.fin || null })) });
      recharger();
    } catch (e) { setMsg(e.response?.data?.error || 'Erreur lors de l\'enregistrement'); } finally { setEnvoi(false); }
  };

  return (
    <>
      <tr className={`${modifie ? 'rm-modifiee' : ''}${!l.groupe && l.v12 ? ' rm-sans' : ''}`}>
        <td className="l rm-marque" onClick={basculer} title={ouverte ? 'Replier' : 'Voir les périodes'}>
          <span className={`rm-chev${ouverte ? ' on' : ''}`}>▸</span><b>{l.marque}</b>
        </td>
        <td>{l.v12 ? nb(l.v12) : <span className="ob-faint">—</span>}</td>
        <td className="l">
          <input list="rm-groupes" value={actuel ? actuel.groupe : ''} disabled={!editable} placeholder="Non rattaché"
            className={!actuel && l.v12 ? 'rm-manquant' : ''} onChange={(e) => setGroupeActuel(e.target.value)} />
        </td>
        <td className="l ob-faint">{actuel ? dateFr(actuel.debut) : '—'}{actuel && actuel.fin ? ` → ${dateFr(actuel.fin)}` : ''}</td>
        <td className="l"><input value={f.concession} disabled={!editable} placeholder="—" onChange={(e) => setF((x) => ({ ...x, concession: e.target.value }))} /></td>
        <td className="l">
          <span className="ob-faint">{l.periodes.length} période{l.periodes.length > 1 ? 's' : ''}</span>
          {editable && modifie && <button type="button" className="ob-btn ob-btn-p rm-save" disabled={envoi} onClick={enregistrer}>{envoi ? '…' : 'Enregistrer'}</button>}
          {editable && modifie && <button type="button" className="ob-btn rm-save" onClick={() => { setF(init); setMsg(''); }}>Annuler</button>}
        </td>
      </tr>
      {msg && <tr className="rm-msg"><td colSpan={6} className="l">⚠️ {msg}</td></tr>}
      {ouverte && (
        <tr className="rm-detail"><td colSpan={6} className="l">
          <table className="rm-periodes">
            <thead><tr><th className="l">Groupe</th><th className="l">Début</th><th className="l">Fin</th><th /></tr></thead>
            <tbody>
              {f.periodes.map((p, i) => (
                <tr key={i}>
                  <td><input list="rm-groupes" value={p.groupe} disabled={!editable} onChange={(e) => setPeriode(i, 'groupe', e.target.value)} /></td>
                  <td><input type="date" value={p.debut} disabled={!editable} onChange={(e) => setPeriode(i, 'debut', e.target.value)} /></td>
                  <td><input type="date" value={p.fin} disabled={!editable} onChange={(e) => setPeriode(i, 'fin', e.target.value)} /><span className="ob-faint rm-enc">{p.fin ? '' : 'en cours'}</span></td>
                  <td>{editable && <button type="button" className="ob-btn" onClick={() => supprimer(i)} title="Supprimer la période" aria-label="Supprimer la période">✕</button>}</td>
                </tr>
              ))}
              {!f.periodes.length && <tr><td colSpan={4} className="ob-faint">Aucune période : la marque est « Non rattachée » dans ce département.</td></tr>}
            </tbody>
          </table>
          {editable && <button type="button" className="ob-btn rm-ajout" onClick={ajouter}>+ Nouvelle période (à partir du 1er du mois)</button>}
        </td></tr>
      )}
    </>
  );
}
